import * as L from 'leaflet';
import { BehaviorSubject } from 'rxjs';

import { MapSelection } from './map-selection';
import { latLngToTile } from './spatial/tile-math';

export interface GridCell {
  readonly zoom: number;
  readonly tileX: number;
  readonly tileY: number;
}

/** Owns selected Web Mercator cells; rendering belongs to ProspectFeature. */
export class GridObjectSelection implements MapSelection<string> {
  private readonly selected = new Map<string, GridCell>();
  private readonly changedSubject = new BehaviorSubject<readonly string[]>([]);
  readonly changed$ = this.changedSubject.asObservable();

  capture(): readonly string[] { return this.ids; }
  restore(ids: readonly string[]): void { this.replace(ids); }

  get ids(): readonly string[] { return [...this.selected.keys()]; }
  get count(): number { return this.selected.size; }
  get cells(): readonly GridCell[] { return [...this.selected.values()]; }

  has(id: string): boolean { return this.selected.has(id); }
  hasCell(zoom: number, tileX: number, tileY: number): boolean {
    return this.selected.has(cellKey(zoom, tileX, tileY));
  }

  replace(ids: readonly string[]): void {
    const next = new Set(ids);
    const changed = new Set([...this.selected.keys(), ...next]);
    if ([...changed].every(id => this.selected.has(id) === next.has(id))) return;
    this.selected.clear();
    for (const id of next) this.selected.set(id, parseCell(id));
    this.changedSubject.next([...changed]);
  }

  append(ids: readonly string[]): void {
    const changed = ids.filter(id => !this.selected.has(id));
    if (!changed.length) return;
    for (const id of changed) this.selected.set(id, parseCell(id));
    this.changedSubject.next(changed);
  }

  remove(ids: readonly string[]): void {
    const changed = ids.filter(id => this.selected.delete(id));
    if (changed.length) this.changedSubject.next(changed);
  }

  clear(): void {
    if (!this.selected.size) return;
    const changed = this.ids;
    this.selected.clear();
    this.changedSubject.next(changed);
  }

  appendBounds(bounds: L.LatLngBounds, zoom: number): void {
    const northWest = latLngToTile(
      bounds.getNorth(),
      bounds.getWest(),
      zoom,
    );
    const southEast = latLngToTile(
      bounds.getSouth(),
      bounds.getEast(),
      zoom,
    );
    const changed = new Set<string>();
    const cells: GridCell[] = [];
    for (let x = Math.min(northWest.tileX, southEast.tileX);
      x <= Math.max(northWest.tileX, southEast.tileX); x++) {
      for (let y = Math.min(northWest.tileY, southEast.tileY);
        y <= Math.max(northWest.tileY, southEast.tileY); y++) {
        cells.push({ zoom, tileX: x, tileY: y });
      }
    }
    this.addCells(cells, changed);
    if (changed.size) this.changedSubject.next([...changed]);
  }

  /**
   * Adds one same-zoom brush result while scanning existing descendants once.
   * Box selection previously scanned the complete selection once per cell.
   */
  private addCells(
    cells: readonly GridCell[],
    changed: Set<string>,
  ): void {
    if (!cells.length) return;
    const zoom = cells[0].zoom;
    const requested = new Set(cells.map(cell =>
      cellKey(cell.zoom, cell.tileX, cell.tileY)));
    for (const [key, candidate] of this.selected) {
      if (candidate.zoom <= zoom) continue;
      const distance = candidate.zoom - zoom;
      if (!requested.has(cellKey(
        zoom,
        candidate.tileX >> distance,
        candidate.tileY >> distance,
      ))) continue;
      this.selected.delete(key);
      changed.add(key);
    }
    for (const cell of cells) {
      const key = cellKey(cell.zoom, cell.tileX, cell.tileY);
      if (this.coveringAncestor(cell.zoom, cell.tileX, cell.tileY)
        || this.selected.has(key)) continue;
      this.selected.set(key, cell);
      changed.add(key);
    }
  }

  private coveringAncestor(
    zoom: number,
    tileX: number,
    tileY: number,
  ): string | null {
    for (let ancestorZoom = zoom - 1;
      ancestorZoom >= zoom - 8 && ancestorZoom >= 0;
      ancestorZoom--) {
      const distance = zoom - ancestorZoom;
      const key = cellKey(
        ancestorZoom,
        tileX >> distance,
        tileY >> distance,
      );
      if (this.selected.has(key)) return key;
    }
    return null;
  }

  /** Selected cells intersecting the current viewport, without projecting all
   * off-screen history on every canvas redraw. */
  *cellsInBounds(bounds: L.LatLngBounds): Iterable<GridCell> {
    const byZoom = new Map<number, GridCell[]>();
    for (const cell of this.selected.values()) {
      const cells = byZoom.get(cell.zoom) ?? [];
      cells.push(cell);
      byZoom.set(cell.zoom, cells);
    }
    for (const [zoom, selected] of byZoom) {
      const northWest = latLngToTile(bounds.getNorth(), bounds.getWest(), zoom);
      const southEast = latLngToTile(bounds.getSouth(), bounds.getEast(), zoom);
      const minX = Math.min(northWest.tileX, southEast.tileX);
      const maxX = Math.max(northWest.tileX, southEast.tileX);
      const minY = Math.min(northWest.tileY, southEast.tileY);
      const maxY = Math.max(northWest.tileY, southEast.tileY);
      const visibleCellCount = (maxX - minX + 1) * (maxY - minY + 1);
      if (visibleCellCount < selected.length) {
        for (let tileX = minX; tileX <= maxX; tileX++) {
          for (let tileY = minY; tileY <= maxY; tileY++) {
            const cell = this.selected.get(cellKey(zoom, tileX, tileY));
            if (cell) yield cell;
          }
        }
        continue;
      }
      for (const cell of selected) {
        if (cell.tileX >= minX && cell.tileX <= maxX
          && cell.tileY >= minY && cell.tileY <= maxY) yield cell;
      }
    }
  }
}

function cellKey(zoom: number, tileX: number, tileY: number): string {
  return zoom + ':' + tileX + ':' + tileY;
}

function parseCell(key: string): GridCell {
  const [zoom, tileX, tileY] = key.split(':').map(Number);
  return { zoom, tileX, tileY };
}
